<template>
  <div v-if="policyStatusRules">
    <div v-for="(item, itemIndex) in statusMessages.fields" :key="itemIndex">
      <div v-if="showItem(item)" class="mt-4" :data-test-id="item.name">
        <InformationBox
          :title="item.title.value"
          :description="fillBookmarks(item)"
          :type="item.messageType.value"
          :isCircle="true"
          :useButtonSlot="hasLinks(item)"
        >
          <template #buttons>
            <FutureChangesLinks
              v-if="[StatusMessages.FutureChanges].indexOf(item.name) > -1"
              :insurancePolicy="insurancePolicy"
              :item="item"
            ></FutureChangesLinks>
            <RenewalUpcomingLinks
              v-if="
                [
                  StatusMessages.RenewalUpcomingInvoiceDirectDebitAutoReviewOn,
                  StatusMessages.RenewalUpcomingDirectDebitAutoReviewOff,
                  StatusMessages.RenewalUpcomingInvoicePaid,
                ].indexOf(item.name) > -1
              "
              :insurancePolicy="insurancePolicy"
              :item="item"
              :renewLink="renewLink"
              :policyStatusRules="policyStatusRules"
            >
            </RenewalUpcomingLinks>
            <PaymentDueLinks
              v-if="
                [
                  StatusMessages.PaymentDueInvoiceSingleInSequence,
                  StatusMessages.PaymentDueInvoiceMultipleInSequence,
                  StatusMessages.PaymentDueSingleAnnualDirectDebitInSequence,
                  StatusMessages.PaymentDueMultipleAnnualDirectDebitInSequence,
                  StatusMessages.PaymentDueInvoiceSingleOutOfSequence,
                  StatusMessages.PaymentDueInvoiceMultipleOutOfSequence,
                  StatusMessages.PaymentDueSingleAnnualDirectDebitOutOfSequence,
                  StatusMessages.PaymentDueMultipleAnnualDirectDebitOutOfSequence,
                  StatusMessages.PaymentOverdueDelinquent,
                ].indexOf(item.name) > -1
              "
              :insurancePolicy="insurancePolicy"
              :item="item"
              :selfServiceToggles="selfServiceToggles"
            ></PaymentDueLinks>
          </template>
        </InformationBox>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { InsurancePolicy } from '@/interfaces';
import {
  IInsurancePolicyStatusRules,
  isValidDelinquency,
  isAutoCancelledPolicyInGracePeriod,
} from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { formatDate, isSameDay } from 'date-fns';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { formatCurrency } from '@/utils/text-transforms';
import { Discriminators } from '@/constants/insurance-policy-const';
import { PolicyStatus } from '@/enums/policy-status.enum';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import FutureChangesLinks from './FutureChangesLinks.vue';
import RenewalUpcomingLinks from './RenewalUpcomingLinks.vue';
import PaymentDueLinks from './PaymentDueLinks.vue';
import { minDate } from '@/utils/common-helper';
import { LONG_MONTH_DATE } from '@/constants/formats';

enum Bookmarks {
  renewalStartDate = 'renewalStartDate',
  currentEndDate = 'currentEndDate',
  cancellationDate = 'cancellationDate',
  termEndDate = 'termEndDate',
  numberOfCoverages = 'numberOfCoverages',
  policyId = 'policyId',
  coverageId = 'coverageId',
  policyNumber = 'policyNumber',
  amountDue = 'amountDue',
  dueDate = 'dueDate',
  renewalDocumentLink = 'renewalDocumentLink',

  futureChangeEffectiveDate = 'futureChange.effectiveDate',
  overdueDate = 'overdueDate',
}

enum StatusMessagesGroup {
  FutureChanges,
  PolicyCancellation,
  PolicyOverdue,
  RenewalUpcoming,
  PaymentDue,
  CycloneUpdate,
}

enum StatusMessages {
  FutureChanges = 'futureChanges',
  policyCancelledManual = 'policyCancelledManual',
  policyCancelledAuto = 'policyCancelledAuto',
  PolicyCancellationInvoice = 'policyCancellationInvoice',
  PolicyCancellationMonthlyDirectDebit = 'policyCancellationMonthlyDirectDebit',
  PolicyCancellationAnnualDirectDebit = 'policyCancellationAnnualDirectDebit',
  PolicyOverdueInvoice = 'policyOverdueInvoice',
  PolicyOverdueDirectDebit = 'policyOverdueDirectDebit',
  PolicyOverdueDelinquent = 'policyOverdueDelinquent',
  RenewalUpcomingInvoicePaid = 'renewalUpcomingInvoicePaid',
  RenewalUpcomingInvoiceDirectDebitAutoReviewOn = 'renewalUpcomingInvoiceDirectDebitAutoReviewOn',
  RenewalUpcomingDirectDebitAutoReviewOff = 'renewalUpcomingDirectDebitAutoReviewOff',
  RenewalUpcomingDirectDebitAutoReviewOffAccepted = 'renewalUpcomingDirectDebitAutoReviewOffAccepted',
  RenewalUpcomingDirectDebitAutoReviewOffRejected = 'renewalUpcomingDirectDebitAutoReviewOffRejected',
  PaymentDueInvoiceSingleInSequence = 'paymentDueInvoiceSingleInSequence',
  PaymentDueInvoiceMultipleInSequence = 'paymentDueInvoiceMultipleInSequence',
  PaymentDueSingleAnnualDirectDebitInSequence = 'paymentDueSingleAnnualDirectDebitInSequence',
  PaymentDueMultipleAnnualDirectDebitInSequence = 'paymentDueMultipleAnnualDirectDebitInSequence',
  PaymentDueInvoiceSingleOutOfSequence = 'paymentDueInvoiceSingleOutOfSequence',
  PaymentDueInvoiceMultipleOutOfSequence = 'paymentDueInvoiceMultipleOutOfSequence',
  PaymentDueSingleAnnualDirectDebitOutOfSequence = 'paymentDueSingleAnnualDirectDebitOutOfSequence',
  PaymentDueMultipleAnnualDirectDebitOutOfSequence = 'paymentDueMultipleAnnualDirectDebitOutOfSequence',
  CycloneUpdateNeeded = 'cycloneUpdateNeeded',
  CycloneUpdateNeededRenewal = 'cycloneUpdateNeededRenewal',
  PaymentOverdueDelinquent = 'paymentOverdueDelinquent',
}

const currentTermResponsive = computed(() => {
  if (!props.insurancePolicy?.currentTermPaymentDetails) return null;
  return props.insurancePolicy?.currentTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
    ? props.insurancePolicy.currentTermPaymentDetails
    : null;
});

const props = defineProps<{
  insurancePolicy: InsurancePolicy;
  statusMessages: any;
  selfServiceToggles: any;
  policyStatusRules: IInsurancePolicyStatusRules;
  renewLink: any;
}>();

const getStatusMessageGroup = (itemName: string) => {
  switch (itemName) {
    case StatusMessages.FutureChanges:
      return StatusMessagesGroup.FutureChanges;
    case StatusMessages.policyCancelledManual:
    case StatusMessages.policyCancelledAuto:
    case StatusMessages.PolicyCancellationInvoice:
    case StatusMessages.PolicyCancellationMonthlyDirectDebit:
    case StatusMessages.PolicyCancellationAnnualDirectDebit:
      return StatusMessagesGroup.PolicyCancellation;
    case StatusMessages.PolicyOverdueInvoice:
    case StatusMessages.PolicyOverdueDirectDebit:
    case StatusMessages.PolicyOverdueDelinquent:
      return StatusMessagesGroup.PolicyOverdue;
    case StatusMessages.RenewalUpcomingInvoicePaid:
    case StatusMessages.RenewalUpcomingInvoiceDirectDebitAutoReviewOn:
    case StatusMessages.RenewalUpcomingDirectDebitAutoReviewOff:
    case StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffAccepted:
    case StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffRejected:
      return StatusMessagesGroup.RenewalUpcoming;
    case StatusMessages.PaymentDueInvoiceSingleInSequence:
    case StatusMessages.PaymentDueInvoiceMultipleInSequence:
    case StatusMessages.PaymentDueSingleAnnualDirectDebitInSequence:
    case StatusMessages.PaymentDueMultipleAnnualDirectDebitInSequence:
    case StatusMessages.PaymentDueInvoiceSingleOutOfSequence:
    case StatusMessages.PaymentDueInvoiceMultipleOutOfSequence:
    case StatusMessages.PaymentDueSingleAnnualDirectDebitOutOfSequence:
    case StatusMessages.PaymentDueMultipleAnnualDirectDebitOutOfSequence:
    case StatusMessages.PaymentOverdueDelinquent:
      return StatusMessagesGroup.PaymentDue;
    case StatusMessages.CycloneUpdateNeeded:
    case StatusMessages.CycloneUpdateNeededRenewal:
      return StatusMessagesGroup.CycloneUpdate;
  }

  return '';
};
/**
 * Determine if the useButtonSlot should be shown
 */
const hasLinks = (item: any): boolean => {
  switch (getStatusMessageGroup(item.name)) {
    case StatusMessagesGroup.FutureChanges:
      return (props.insurancePolicy.futureChanges?.length ?? 0) > 0;
    case StatusMessagesGroup.RenewalUpcoming:
      return (
        (props.policyStatusRules?.showPaymentDue && props.policyStatusRules?.autoRenewActuallyOff) ||
        props.insurancePolicy?.renewalDocumentId !== undefined
      );
    case StatusMessagesGroup.PaymentDue: {
      const responsivePaymentDetails =
        props.insurancePolicy?.currentTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
          ? props.insurancePolicy?.currentTermPaymentDetails
          : null;
      const renewalTermPaymentDetails =
        props.insurancePolicy?.renewalTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
          ? props.insurancePolicy?.renewalTermPaymentDetails
          : null;

      const shouldShowPaymentButton =
        (responsivePaymentDetails?.totalAmountDue ?? 0) > 0 ||
        (renewalTermPaymentDetails?.totalAmountDue ?? 0) > 0 ||
        isValidDelinquency(props.insurancePolicy);

      return (
        shouldShowPaymentButton &&
        item.firstLinkContent.value?.length > 0 &&
        props.insurancePolicy?.currentTermPaymentDetails?.paymentReferenceNumber?.length > 0
      );
    }
  }

  return false;
};

const showItem = (item: any) => {
  let showMessage = false;
  switch (getStatusMessageGroup(item.name)) {
    case StatusMessagesGroup.FutureChanges:
      showMessage = props.policyStatusRules?.showFutureChange ?? false;
      break;
    case StatusMessagesGroup.PolicyCancellation:
      {
        const showFutureCancelled = props.policyStatusRules?.showFutureCancelled ?? false;

        if (showFutureCancelled && item.name === StatusMessages.PolicyCancellationInvoice) {
          showMessage = currentTermResponsive.value !== null;
        } else if (showFutureCancelled && item.name == StatusMessages.PolicyCancellationMonthlyDirectDebit) {
          showMessage =
            currentTermResponsive.value == null && !props.insurancePolicy.currentTermPaymentDetails.isAnnual;
        } else if (showFutureCancelled && item.name == StatusMessages.PolicyCancellationAnnualDirectDebit) {
          showMessage =
            showFutureCancelled &&
            currentTermResponsive.value === null &&
            props.insurancePolicy.currentTermPaymentDetails.isAnnual;
        } else if (item.name === StatusMessages.policyCancelledAuto) {
          // SSPCI.FR3.8
          showMessage = isAutoCancelledPolicyInGracePeriod(props.insurancePolicy);
        } else if (item.name === StatusMessages.policyCancelledManual) {
          // SSPCI.FR3.9
          const cancellationDate = props.insurancePolicy.cancellationDate
            ? new Date(props.insurancePolicy.cancellationDate)
            : null;
          const cancelledToday = cancellationDate ? isSameDay(cancellationDate, new Date()) : false;
          if (props.insurancePolicy.status === PolicyStatus.CANCELLED && cancelledToday) {
            const renewalTermStart = props.insurancePolicy.renewalTermStart
              ? new Date(props.insurancePolicy.renewalTermStart)
              : null;
            const cancellationMatchesRenewalStart =
              cancellationDate && renewalTermStart ? isSameDay(cancellationDate, renewalTermStart) : false;
            const renewalPaymentIsResponsive =
              props.insurancePolicy.renewalTermPaymentDetails?.discriminator ===
              Discriminators.ResponsivePaymentDetails;
            const isMidTerm = !props.insurancePolicy.state.isInRenewal;
            const isRenewalNonMatchingDate =
              props.insurancePolicy.state.isInRenewal && !cancellationMatchesRenewalStart;
            const isRenewalMatchingDateManualPayment =
              props.insurancePolicy.state.isInRenewal && cancellationMatchesRenewalStart && renewalPaymentIsResponsive;
            const isRenewalMatchingDateAutoRenewOn =
              props.insurancePolicy.state.isInRenewal &&
              cancellationMatchesRenewalStart &&
              !renewalPaymentIsResponsive &&
              props.insurancePolicy.currentRenewalPreference === true;

            showMessage =
              isMidTerm ||
              isRenewalNonMatchingDate ||
              isRenewalMatchingDateManualPayment ||
              isRenewalMatchingDateAutoRenewOn;
          }
        }
      }
      break;

    case StatusMessagesGroup.PolicyOverdue:
      {
        const showOverdue = props.policyStatusRules?.showOverdue ?? false;

        if (showOverdue && item.name === StatusMessages.PolicyOverdueInvoice) {
          showMessage = showOverdue && currentTermResponsive.value !== null;
        } else if (showOverdue && item.name === StatusMessages.PolicyOverdueDelinquent) {
          showMessage = props.policyStatusRules?.isValidDelinquency;
        } else if (showOverdue && item.name === StatusMessages.PolicyOverdueDirectDebit) {
          showMessage =
            showOverdue && currentTermResponsive.value === null && !props.policyStatusRules?.isValidDelinquency;
        }
      }
      break;

    case StatusMessagesGroup.RenewalUpcoming:
      {
        const showIsInRenewal = props.policyStatusRules?.showIsInRenewal ?? false;
        const autoRenewIsOn =
          props.insurancePolicy.currentRenewalPreference !== undefined
            ? props.insurancePolicy.currentRenewalPreference
            : null;

        if (showIsInRenewal) {
          //Green message as paid in advance.
          if (item.name === StatusMessages.RenewalUpcomingInvoicePaid) {
            showMessage =
              currentTermResponsive.value !== null && !props.insurancePolicy?.renewalTermPaymentDetails?.isPaymentDue;
          } else if (item.name === StatusMessages.RenewalUpcomingInvoiceDirectDebitAutoReviewOn) {
            showMessage =
              (currentTermResponsive.value !== null &&
                props.insurancePolicy?.renewalTermPaymentDetails?.isPaymentDue) ||
              (currentTermResponsive.value === null && autoRenewIsOn === true) ||
              (currentTermResponsive.value === null && autoRenewIsOn === null);
          } else if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOff) {
            showMessage =
              (currentTermResponsive.value === null &&
                autoRenewIsOn !== null &&
                autoRenewIsOn === false &&
                props.insurancePolicy.renewalTermAcceptance === undefined) ??
              false;
          } else if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffAccepted) {
            showMessage =
              currentTermResponsive.value === null &&
              autoRenewIsOn !== null &&
              autoRenewIsOn === false &&
              props.insurancePolicy.renewalTermAcceptance === true;
          } else if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffRejected) {
            showMessage =
              currentTermResponsive.value === null &&
              autoRenewIsOn !== null &&
              autoRenewIsOn === false &&
              props.insurancePolicy.renewalTermAcceptance === false;
          }
        }
      }
      break;

    case StatusMessagesGroup.PaymentDue:
      showMessage = determinePaymentDueContentToShow(item);

      break;

    case StatusMessagesGroup.CycloneUpdate:
      if (
        item.name === StatusMessages.CycloneUpdateNeededRenewal &&
        props.policyStatusRules?.showCycloneBanner &&
        props.insurancePolicy.state.isInRenewal
      ) {
        showMessage = true;
      } else if (
        item.name === StatusMessages.CycloneUpdateNeeded &&
        props.policyStatusRules?.showCycloneBanner &&
        !props.insurancePolicy.state.isInRenewal
      ) {
        showMessage = true;
      }

      break;

    default:
      return showMessage;
  }

  return showMessage;
};

const fillBookmarks = (item: any) => {
  let content = item.content.value;

  switch (getStatusMessageGroup(item.name)) {
    case StatusMessagesGroup.FutureChanges:
      break;
    case StatusMessagesGroup.PolicyCancellation:
      {
        const cancellationDate = props.insurancePolicy?.cancellationDate
          ? formatDate(new Date(props.insurancePolicy.cancellationDate), LONG_MONTH_DATE)
          : null;

        if (cancellationDate) {
          content = replaceBookmark(Bookmarks.cancellationDate, content, cancellationDate);
        }
      }
      break;
    case StatusMessagesGroup.PolicyOverdue:
      {
        const responsivePaymentDetails =
          currentTermResponsive.value !== null ? props.insurancePolicy.currentTermPaymentDetails : null;

        if (responsivePaymentDetails) {
          const overdueInceptionDate = responsivePaymentDetails.overdueInceptionDate
            ? formatDate(new Date(responsivePaymentDetails.overdueInceptionDate), LONG_MONTH_DATE).toString()
            : '';

          content = replaceBookmark(Bookmarks.overdueDate, content, overdueInceptionDate);

          content = replaceBookmark(
            Bookmarks.amountDue,
            content,
            formatCurrency(responsivePaymentDetails.overdueAmount, 2)
          );
        }
      }
      break;

    case StatusMessagesGroup.RenewalUpcoming:
      {
        const renewalTermStart = props.insurancePolicy?.renewalTermStart
          ? formatDate(new Date(props.insurancePolicy.renewalTermStart), LONG_MONTH_DATE).toString()
          : null;

        if (renewalTermStart) {
          content = replaceBookmark(Bookmarks.renewalStartDate, content, renewalTermStart);
        }

        if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOff) {
          //Hide content when Renew button is not shown
          //Mirrors logic in RenewalUpcomingLinks component showRenewButton function.
          // Note: Insurance Product Card uses renewalTermStart this is in effect the same date as the termEnd date which is what is used on the Product Details page
          // just mentioning to prevent any confusion in future as similar logic applies between the the components.
          if (!props.policyStatusRules?.showPaymentDue || !props.policyStatusRules?.autoRenewActuallyOff) {
            content = content.replace(/<span id="renewYourPolicyNow">[\s\S]*?<\/span>/g, '');
          }
        } else if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffAccepted) {
          const currentEndDate = props.insurancePolicy?.termEnd
            ? formatDate(new Date(props.insurancePolicy.termEnd), LONG_MONTH_DATE).toString()
            : null;

          if (currentEndDate) {
            content = replaceBookmark(Bookmarks.currentEndDate, content, currentEndDate);
          }
        } else if (item.name === StatusMessages.RenewalUpcomingDirectDebitAutoReviewOffRejected) {
          const currentEndDate = props.insurancePolicy?.termEnd
            ? formatDate(new Date(props.insurancePolicy.termEnd), LONG_MONTH_DATE).toString()
            : null;

          if (currentEndDate) {
            content = replaceBookmark(Bookmarks.currentEndDate, content, currentEndDate);
          }
        }
      }
      break;

    case StatusMessagesGroup.PaymentDue:
      content = fillPaymentDueDetails(content);
      break;

    case StatusMessagesGroup.CycloneUpdate:
      content = replaceBookmark(Bookmarks.policyNumber, content, props.insurancePolicy?.policyNumber);
      content = replaceBookmark(Bookmarks.coverageId, content, props.insurancePolicy?.coverages[0].id);
      if (props.insurancePolicy?.renewalTermStart) {
        const renewalTermStart = props.insurancePolicy?.renewalTermStart
          ? formatDate(new Date(props.insurancePolicy.renewalTermStart), LONG_MONTH_DATE).toString()
          : null;

        if (renewalTermStart) {
          content = replaceBookmark(Bookmarks.renewalStartDate, content, renewalTermStart);
        }
      }

      break;
  }

  return content;
};

const getCurrentTermAndRenewalTermDetails = () => {
  const currentPaymentTermDetails = {
    nextInvoiceDate: props.insurancePolicy.currentTermPaymentDetails.isPaymentDue
      ? props.insurancePolicy.currentTermPaymentDetails.nextInvoiceDueDate
      : null,
    nextInvoiceAmount:
      props.insurancePolicy.currentTermPaymentDetails.discriminator === Discriminators.ResponsivePaymentDetails
        ? props.insurancePolicy.currentTermPaymentDetails.totalAmountDue
        : props.insurancePolicy.currentTermPaymentDetails.nextInvoiceAmountDue,
    hasMultiple: props.insurancePolicy?.currentTermPaymentDetails?.hasMultiplePaymentsDue ?? false,
    isResponsive:
      props.insurancePolicy.currentTermPaymentDetails.discriminator === Discriminators.ResponsivePaymentDetails,
    nonResponsive:
      props.insurancePolicy.currentTermPaymentDetails.discriminator === Discriminators.NonResponsivePaymentDetails,
    isAnnual: props.insurancePolicy.currentTermPaymentDetails?.isAnnual ?? false,
  };

  const renewalTermPaymentDetails = {
    nextInvoiceDate: props.insurancePolicy?.renewalTermPaymentDetails?.isPaymentDue
      ? (props.insurancePolicy.renewalTermPaymentDetails.nextInvoiceDueDate ?? minDate)
      : minDate,
    nextInvoiceAmount:
      props.insurancePolicy.renewalTermPaymentDetails?.discriminator === Discriminators.ResponsivePaymentDetails
        ? props.insurancePolicy.renewalTermPaymentDetails?.totalAmountDue
        : props.insurancePolicy.renewalTermPaymentDetails?.nextInvoiceAmountDue,
    hasMultiple: props.insurancePolicy?.renewalTermPaymentDetails?.hasMultiplePaymentsDue ?? false,
  };

  return {
    currentPaymentTermDetails,
    renewalTermPaymentDetails,
  };
};

const fillPaymentDueDetails = (content: string) => {
  let nextInvoiceDate = null;
  let nextInvoiceAmount = 0;

  const paymentDetails = getCurrentTermAndRenewalTermDetails();

  const renewalTermIsClosestDate =
    !paymentDetails.currentPaymentTermDetails.nextInvoiceDate ||
    (paymentDetails.renewalTermPaymentDetails.nextInvoiceDate > minDate &&
      paymentDetails.currentPaymentTermDetails.nextInvoiceDate >
        paymentDetails.renewalTermPaymentDetails.nextInvoiceDate)
      ? true
      : false;

  if (renewalTermIsClosestDate) {
    nextInvoiceDate = paymentDetails.renewalTermPaymentDetails.nextInvoiceDate;
    nextInvoiceAmount = paymentDetails.renewalTermPaymentDetails.nextInvoiceAmount ?? 0;
  } else {
    nextInvoiceDate = paymentDetails.currentPaymentTermDetails.nextInvoiceDate;
    nextInvoiceAmount = paymentDetails.currentPaymentTermDetails.nextInvoiceAmount ?? 0;
  }

  if (nextInvoiceAmount > 0) {
    content = replaceBookmark(Bookmarks.amountDue, content, formatCurrency(nextInvoiceAmount, 2));
  }

  if (nextInvoiceDate !== minDate) {
    content = replaceBookmark(Bookmarks.dueDate, content, formatDate(new Date(nextInvoiceDate ?? ''), LONG_MONTH_DATE));
  }

  return content;
};

const determinePaymentDueContentToShow = (item: any) => {
  let showItem = false;
  const paymentDetails = getCurrentTermAndRenewalTermDetails();

  const autoRenewIsOn = props.insurancePolicy.currentRenewalPreference ?? false;
  const isNonResponsiveMonthly =
    paymentDetails.currentPaymentTermDetails.nonResponsive && !paymentDetails.currentPaymentTermDetails.isAnnual;
  const isNonResonsiveAnnualAutoRenewOff =
    paymentDetails.currentPaymentTermDetails.nonResponsive &&
    paymentDetails.currentPaymentTermDetails.isAnnual &&
    !autoRenewIsOn;

  if (
    (!props.insurancePolicy.currentTermPaymentDetails?.isPaymentDue &&
      !props.insurancePolicy.renewalTermPaymentDetails?.isPaymentDue) ||
    (isNonResponsiveMonthly && !isValidDelinquency(props.insurancePolicy)) ||
    isNonResonsiveAnnualAutoRenewOff
  ) {
    return showItem;
  }

  const validMessage = [];
  const hasOutofSequenceFutureChanges = props.insurancePolicy.state.hasMultipleOutOfSequenceFutureChanges;
  const hasMultiple =
    paymentDetails.currentPaymentTermDetails.hasMultiple || paymentDetails.renewalTermPaymentDetails.hasMultiple;

  const showDelinquent = isValidDelinquency(props.insurancePolicy);
  if (showDelinquent) validMessage.push(StatusMessages.PaymentOverdueDelinquent);

  const showPaymentDueInvoiceSingleInSequence =
    paymentDetails.currentPaymentTermDetails.isResponsive && !hasMultiple && !hasOutofSequenceFutureChanges;
  if (showPaymentDueInvoiceSingleInSequence) validMessage.push(StatusMessages.PaymentDueInvoiceSingleInSequence);

  const showPaymentDueInvoiceMultipleInSequence =
    paymentDetails.currentPaymentTermDetails.isResponsive && hasMultiple && !hasOutofSequenceFutureChanges;
  if (showPaymentDueInvoiceMultipleInSequence) validMessage.push(StatusMessages.PaymentDueInvoiceMultipleInSequence);

  const showPaymentDueSingleAnnualDirectDebitInSequence =
    paymentDetails.currentPaymentTermDetails.nonResponsive &&
    !hasMultiple &&
    !hasOutofSequenceFutureChanges &&
    !showDelinquent;
  if (showPaymentDueSingleAnnualDirectDebitInSequence)
    validMessage.push(StatusMessages.PaymentDueSingleAnnualDirectDebitInSequence);

  const showPaymentDueMultipleAnnualDirectDebitInSequence =
    paymentDetails.currentPaymentTermDetails.nonResponsive &&
    hasMultiple &&
    !hasOutofSequenceFutureChanges &&
    !showDelinquent;
  if (showPaymentDueMultipleAnnualDirectDebitInSequence)
    validMessage.push(StatusMessages.PaymentDueMultipleAnnualDirectDebitInSequence);

  const showPaymentDueInvoiceSingleOutOfSequence =
    paymentDetails.currentPaymentTermDetails.isResponsive && !hasMultiple && hasOutofSequenceFutureChanges;
  if (showPaymentDueInvoiceSingleOutOfSequence) validMessage.push(StatusMessages.PaymentDueInvoiceSingleOutOfSequence);

  const showPaymentDueInvoiceMultipleOutOfSequence =
    paymentDetails.currentPaymentTermDetails.isResponsive &&
    paymentDetails.currentPaymentTermDetails.hasMultiple &&
    hasOutofSequenceFutureChanges;
  if (showPaymentDueInvoiceMultipleOutOfSequence)
    validMessage.push(StatusMessages.PaymentDueInvoiceMultipleOutOfSequence);

  const showPaymentDueSingleAnnualDirectDebitOutOfSequence =
    paymentDetails.currentPaymentTermDetails.nonResponsive && !hasMultiple && hasOutofSequenceFutureChanges;
  if (showPaymentDueSingleAnnualDirectDebitOutOfSequence)
    validMessage.push(StatusMessages.PaymentDueSingleAnnualDirectDebitOutOfSequence);

  const showPaymentDueMultipleAnnualDirectDebitOutOfSequence =
    paymentDetails.currentPaymentTermDetails.nonResponsive && hasMultiple && hasOutofSequenceFutureChanges;
  if (showPaymentDueMultipleAnnualDirectDebitOutOfSequence)
    validMessage.push(StatusMessages.PaymentDueMultipleAnnualDirectDebitOutOfSequence);

  return validMessage.findIndex((x) => x.toLowerCase() === item.name.toLowerCase()) > -1;
};
</script>
